import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import type { SxProps, Theme } from '@mui/material/styles'

interface Props {
  src?: string | null
  alt?: string
  imageSx: SxProps<Theme>
  placeholderSx: SxProps<Theme>
  testId?: string
}

export function BrandLogoImage({
  src,
  alt = '',
  imageSx,
  placeholderSx,
  testId,
}: Readonly<Props>) {
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setLoaded(false)
    setFailed(false)
  }, [src])

  if (!src || failed) {
    return (
      <Box
        sx={placeholderSx}
        aria-hidden
        data-testid={testId ? `${testId}-placeholder` : undefined}
      />
    )
  }

  return (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        overflow: 'hidden',
        ...imageSx,
      }}
    >
      {loaded ? null : (
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
          }}
          aria-busy
          aria-label="Loading brand logo"
          data-testid={testId ? `${testId}-loading` : undefined}
        />
      )}
      <Box
        component="img"
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: 'inherit',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
        data-testid={testId}
      />
    </Box>
  )
}
